import React, { useCallback, useMemo } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useIntl } from 'react-intl';

import ComponentBlocker from '@src/components/ComponentBlocker';
import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { USER_ROW_ATTENDANCE_STATE } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../../WorkJourneyRealTime.module.less';
import { toUserStatusInput } from '../../utils/attendanceMetrics';
import { resolveUserRowAttendanceState } from '../../utils/attendanceStatus/resolveAttendanceStatus';
import { menuItemIcon } from '../../utils/menuItemIcon';

const USER_ROW_ACTIONS = {
  VIEW: 'view',
  MARK: 'mark',
  REPORT_ABSENCE: 'reportAbsence',
  REPLACE: 'replace',
} as const;

interface WorkJourneyRealTimeUserActionsProps {
  record: AttendanceDashboardItem;
  onMarkUser?: (item: AttendanceDashboardItem) => void;
  onReportAbsence?: (item: AttendanceDashboardItem) => void;
  onReplaceUser?: (item: AttendanceDashboardItem) => void;
}

const WorkJourneyRealTimeUserActions = ({
  record,
  onMarkUser,
  onReportAbsence,
  onReplaceUser,
}: WorkJourneyRealTimeUserActionsProps) => {
  const intl = useIntl();

  const menuItems = useMemo((): MenuProps['items'] => {
    const userStatusInput = toUserStatusInput(record);
    const state = resolveUserRowAttendanceState(userStatusInput);
    const isAbsent = state.code === USER_ROW_ATTENDANCE_STATE.ABSENT;
    const hasJourneyEnded = userStatusInput.actualEndAtSeconds !== null;

    const items: NonNullable<MenuProps['items']> = [];

    // Homologous to header: mark/view → report absence → replace (destructive last).
    if (hasJourneyEnded && !isAbsent) {
      items.push({
        key: USER_ROW_ACTIONS.VIEW,
        label: intl.formatMessage({ id: 'workJourney.realTime.action.view' }),
        icon: menuItemIcon('visibility'),
      });
    } else {
      items.push({
        key: USER_ROW_ACTIONS.MARK,
        label: intl.formatMessage({ id: 'workJourney.realTime.markAttendance.markUser' }),
        icon: menuItemIcon('how_to_reg'),
      });
    }

    items.push({
      key: USER_ROW_ACTIONS.REPORT_ABSENCE,
      label: intl.formatMessage({ id: 'workJourney.realTime.reportAbsence.action' }),
      icon: menuItemIcon('person_remove'),
    });

    items.push({
      key: USER_ROW_ACTIONS.REPLACE,
      label: intl.formatMessage({ id: 'workJourney.realTime.action.replace' }),
      icon: menuItemIcon('swap_horiz'),
      danger: true,
    });

    return items;
  }, [intl, record]);

  const handleMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (key === USER_ROW_ACTIONS.VIEW || key === USER_ROW_ACTIONS.MARK) {
        onMarkUser?.(record);
        return;
      }
      if (key === USER_ROW_ACTIONS.REPORT_ABSENCE) {
        onReportAbsence?.(record);
        return;
      }
      if (key === USER_ROW_ACTIONS.REPLACE) {
        onReplaceUser?.(record);
      }
    },
    [onMarkUser, onReportAbsence, onReplaceUser, record],
  );

  return (
    <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
      <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
        <Button type="primary" size="small" className={`gx-mb-0 ${styles.actionsButton}`}>
          <IntlMessages id="general.actions" />
          <DownOutlined />
        </Button>
      </Dropdown>
    </ComponentBlocker>
  );
};

export default WorkJourneyRealTimeUserActions;
